import { Lightning } from '@lightningjs/sdk';
import Tile, { TileTemplateSpec } from 'components/common/tiles/Tile';
import { Video, Episode } from 'types/api/media';
import {
  isMovie,
  getSeasonEpisodeFormatted,
  isMediaExpiring,
  getExpiringDateString,
  isVideo,
} from 'support/contentUtils';
import MediaProgressBar from 'components/common/progressBar/MediaProgressBar';
import { STANDARD_FADE } from 'support/animations';
import { SelectItemContext } from 'types/events';
import { getImageTextureObj } from 'support/generalUtils';
import { constants } from 'aliases';
import Badge from 'components/common/Badge';
import { translate } from 'support/translate';
import { forceImageNoBadge, updateImageSize } from 'support/cwImageUtils';
import { secondsToContentDuration } from 'support/timeUtils';
import TextLabel from '../TextLabel';

export interface ContinueTileTemplateSpec extends TileTemplateSpec {
  data: Episode | Video;

  ShowThumbnail: Lightning.Texture;
  PlayButton: Lightning.Texture;
  Badge: typeof Badge;
  ProgressBar: typeof MediaProgressBar;
  TextContainer: {
    Title: typeof TextLabel;
    SeasonEpisode: typeof TextLabel;
  };
}

const BADGE_PADDING = 5;

const PROGRESS_BAR_WIDTH = MediaProgressBar.defaultWidth;
const PROGRESS_BAR_HEIGHT = MediaProgressBar.defaultHeight;
const PROGRESS_BAR_MIN_PERCENTAGE = 0.05;

const PLAY_BUTTON_WIDTH = 34;
const PLAY_BUTTON_HEIGHT = 43;

export default class AbstractContinueTile
  extends Tile<ContinueTileTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<ContinueTileTemplateSpec>
{
  protected tileWidth = 0;
  protected tileHeight = 0;
  protected tileHighlightWidth = 0;
  protected tileHighlightHeight = 0;
  protected progressBarX = 0;
  protected progressBarY = 0;

  private _ShowThumbnail = this.getByRef('ShowThumbnail')!;
  protected _PlayButton = this.getByRef('PlayButton')!;
  protected _Badge = this.getByRef('Badge')!;
  protected _ProgressBar = this.getByRef('ProgressBar')!;
  private _TextContainer = this.getByRef('TextContainer')!;
  private _SeasonEpisode = this._TextContainer.getByRef('SeasonEpisode')!;
  private _Title = this._TextContainer.getByRef('Title')!;

  override get title() {
    if (!this._data) return '';

    return [
      this._Title.text?.text ?? '',
      this._SeasonEpisode.text?.text ?? '',
      isMovie(this._data) ? '' : this._data.title ?? '',
      this._ProgressBar.title ?? '',
    ];
  }

  override set data(data: ContinueTileTemplateSpec['data']) {
    super.data = data;

    const showThumbnail = updateImageSize(
      data.images.image_promo,
      this.tileWidth,
    );
    const src = forceImageNoBadge(showThumbnail);

    const { seriesName } = data;
    this._ShowThumbnail.patch({ src });
    this._Title.patch({ text: { text: seriesName } });
    this._ProgressBar.patch({ mediaItem: data });

    const visible = this.shouldShowProgressBar();
    this._ProgressBar.patch({ visible });

    if (isVideo(data)) {
      const video = data as Video;
      const isNew = video.isNew;
      const expiring = isMediaExpiring(video);

      if (isNew) {
        this._Badge.label = translate('episode.new');
      } else if (expiring) {
        this._Badge.label = getExpiringDateString(video);
      }
    }

    const strDuration = secondsToContentDuration(Number(data.durationSecs));
    if (isMovie(data)) {
      this._SeasonEpisode.patch({ text: { text: strDuration } });
    } else {
      const seasonEpisode = getSeasonEpisodeFormatted(data);
      this._SeasonEpisode.patch({
        text: { text: `${seasonEpisode} • ${strDuration}` },
      });
    }
  }

  override get data() {
    return super.data;
  }

  override get action(): SelectItemContext['action'] {
    return 'play';
  }

  static override _template(): Lightning.Component.Template<ContinueTileTemplateSpec> {
    const playButtonTexture = getImageTextureObj(
      'static/images/playback/player-play-icon.svg',
      PLAY_BUTTON_WIDTH,
      PLAY_BUTTON_HEIGHT,
    );

    return {
      ...super._template(),
      ShowThumbnail: {
        alpha: 1,
        zIndex: constants.ui.zIndex.foregroundLayer + 2,
        w: this.highlightWidth,
        h: this.highlightHeight,
        transitions: STANDARD_FADE,
      },
      PlayButton: {
        alpha: constants.ui.invisible,
        mount: 0.5,
        ...playButtonTexture,
      },
      Badge: {
        zIndex: constants.ui.zIndex.floatingLayer,
        type: Badge,
        mountX: 1,
        x: w => w - BADGE_PADDING,
        y: BADGE_PADDING,
      },
      ProgressBar: {
        type: MediaProgressBar,
        zIndex: constants.ui.zIndex.floatingLayer - 1,
      },
    };
  }

  override _setup() {
    this.progressBarX = (this.tileWidth - PROGRESS_BAR_WIDTH) / 2;
    this.progressBarY = this.tileHighlightHeight - PROGRESS_BAR_HEIGHT - 10;

    this._PlayButton.patch({
      x: this.tileHighlightWidth / 2,
      y: this.tileHighlightHeight / 2,
    });
    this._ProgressBar.patch({ x: this.progressBarX, y: this.progressBarY });

    super._setup();
  }

  override _active() {
    super._active();

    this.updateImageUrl();
    this.updatePlayButton();
  }

  override _focus() {
    this.updateImageUrl();
    this.updatePlayButton();
  }

  override _unfocus() {
    this.updateImageUrl();
    this.updatePlayButton();
  }

  private updateImageUrl() {
    const showThumbnailAlpha = this.hasFocus() ? constants.ui.invisible : 1;
    this._ShowThumbnail.setSmooth('alpha', showThumbnailAlpha);
  }

  private updatePlayButton() {
    const playButtonAlpha = this.hasFocus() ? 0.6 : constants.ui.invisible;
    this._PlayButton.setSmooth('alpha', playButtonAlpha);
  }

  protected shouldShowProgressBar() {
    return this._ProgressBar.progressPercent >= PROGRESS_BAR_MIN_PERCENTAGE;
  }
}
